// src/pages/TranslateHubPage.js
// Hub page listing all housing authorities with translate portals
// Includes language selector that persists into org pages

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Brand from '../components/Brand';
import { trackTranslateHubVisit, trackTranslateOrgSelect } from '../utils/analytics';

// ─── Responsive hook ─────────────────────────────────────────────
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

const T = {
  blue600: '#1d4ed8', // blue-700: reads at 4.5:1 on the blue-100 chips (WCAG 1.4.3),
  blue50: '#eff6ff',
  blue100: '#dbeafe',
  gray900: '#111827',
  gray800: '#1f2937',
  gray600: '#4b5563',
  gray500: '#5f6673', // darkened for 4.5:1 on light backgrounds (WCAG 1.4.3),
  gray400: '#6b7280', // darkened for 4.5:1 on white (WCAG 1.4.3),
  gray200: '#e5e7eb',
  gray100: '#f3f4f6',
  white: '#ffffff',
  radius: '16px',
  radiusSm: '12px',
  radiusPill: '999px',
  shadow: '0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
  shadowSm: '0 4px 6px -1px rgba(0,0,0,0.07), 0 2px 4px -2px rgba(0,0,0,0.05)',
  gradient: 'linear-gradient(135deg, #eff6ff 0%, #ffffff 50%, #faf5ff 100%)',
  font: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

const LANG_TO_COUNTRY = {
  en: 'us', es: 'es', zh: 'cn', vi: 'vn',
  ru: 'ru', ar: 'sa', ht: 'ht', pt: 'br', fr: 'fr',
  it: 'it', el: 'gr',
};

const LANGUAGE_NAMES = {
  en: 'English', es: 'Español', zh: '简体中文', vi: 'Tiếng Việt',
  ru: 'Русский', ar: 'العربية', ht: 'Kreyòl Ayisyen',
  pt: 'Português', fr: 'Français',
  it: 'Italiano', el: 'Ελληνικά',
};

const ALL_LANGS = Object.keys(LANGUAGE_NAMES);

function FlagImg({ lang, size = 20 }) {
  const c = LANG_TO_COUNTRY[lang] || 'us';
  return (
    <img
      src={`https://flagcdn.com/w40/${c}.png`}
      srcSet={`https://flagcdn.com/w80/${c}.png 2x`}
      alt=""
      width={size}
      height={Math.round(size * 0.75)}
      style={{ objectFit: 'cover', borderRadius: 2, verticalAlign: 'middle', flexShrink: 0 }}
    />
  );
}

// ─── UI label translations for hub page ─────────────────────────
const HUB_LABELS = {
  en: {
    portalBadge: 'AI Translation Portal',
    heroTitle: 'AI-Translated Housing Resources',
    heroDescription: 'We collect all public-facing webpages, forms, and documents from each housing authority, transcribe them into English using AI, and translate them into 10+ languages. Each translation includes side-by-side viewing and direct links to the original official source documents.',
    heroDisclaimer: 'All English transcriptions and translations are AI-generated and may contain errors or inaccuracies. These are not official documents and should not be relied upon as legally binding. Always refer to the original documents from the housing authority for official use. HousingAI is not responsible for any damages or consequences resulting from the use of these translations.',
    selectPrompt: 'Select a housing authority below to get started:',
    housingAuthority: 'Housing Authority',
    allRights: 'All rights reserved.',
    searchPlaceholder: 'Search housing authorities…',
    noResults: 'No housing authorities found',
  },
  es: {
    portalBadge: 'Portal de Traducción IA',
    heroTitle: 'Recursos de Vivienda Traducidos por IA',
    heroDescription: 'Recopilamos todas las páginas web, formularios y documentos públicos de cada autoridad de vivienda, los transcribimos al inglés mediante IA y los traducimos a más de 10 idiomas. Cada traducción incluye vista lado a lado y enlaces directos a los documentos oficiales originales.',
    heroDisclaimer: 'Todas las transcripciones y traducciones son generadas por IA y pueden contener errores o inexactitudes. Estos no son documentos oficiales y no deben considerarse legalmente vinculantes. Consulte siempre los documentos originales de la autoridad de vivienda para uso oficial. HousingAI no es responsable de ningún daño o consecuencia derivada del uso de estas traducciones.',
    selectPrompt: 'Seleccione una autoridad de vivienda para comenzar:',
    housingAuthority: 'Autoridad de Vivienda',
    allRights: 'Todos los derechos reservados.',
    searchPlaceholder: 'Buscar autoridades de vivienda…',
    noResults: 'No se encontraron autoridades de vivienda',
  },
  fr: {
    portalBadge: 'Portail de Traduction IA',
    heroTitle: 'Ressources de Logement Traduites par IA',
    heroDescription: 'Nous collectons toutes les pages web, formulaires et documents publics de chaque office du logement, les transcrivons en anglais par IA, puis les traduisons dans plus de 10 langues. Chaque traduction comprend un affichage côte à côte et des liens directs vers les documents officiels originaux.',
    heroDisclaimer: 'Toutes les transcriptions et traductions sont générées par IA et peuvent contenir des erreurs ou inexactitudes. Ce ne sont pas des documents officiels et ne doivent pas être considérés comme juridiquement contraignants. Référez-vous toujours aux documents originaux de l\'office du logement pour un usage officiel. HousingAI décline toute responsabilité pour tout dommage ou conséquence résultant de l\'utilisation de ces traductions.',
    selectPrompt: 'Sélectionnez un office du logement pour commencer\u00a0:',
    housingAuthority: 'Office du Logement',
    allRights: 'Tous droits réservés.',
    searchPlaceholder: 'Rechercher un office du logement…',
    noResults: 'Aucun office du logement trouvé',
  },
  zh: {
    portalBadge: 'AI翻译门户',
    heroTitle: 'AI翻译住房资源',
    heroDescription: '我们收集每个住房管理局的所有公开网页、表格和文件，使用AI将其转录为英文，并翻译成10多种语言。每份翻译都包含并排对照视图和指向原始官方文件的直接链接。',
    heroDisclaimer: '所有英文转录和翻译均由AI生成，可能包含错误或不准确之处。这些不是官方文件，不应被视为具有法律约束力。请始终参阅住房管理局的原始文件以作官方用途。HousingAI对因使用这些翻译而产生的任何损害或后果不承担责任。',
    selectPrompt: '选择一个住房管理局开始浏览：',
    housingAuthority: '住房管理局',
    allRights: '版权所有。',
    searchPlaceholder: '搜索住房管理局…',
    noResults: '未找到住房管理局',
  },
  vi: {
    portalBadge: 'Cổng Dịch Thuật AI',
    heroTitle: 'Tài Nguyên Nhà Ở Được Dịch Bởi AI',
    heroDescription: 'Chúng tôi thu thập tất cả các trang web, biểu mẫu và tài liệu công khai từ mỗi cơ quan nhà ở, chuyển ngữ chúng sang tiếng Anh bằng AI, và dịch sang hơn 10 ngôn ngữ. Mỗi bản dịch bao gồm chế độ xem song song và liên kết trực tiếp đến tài liệu gốc chính thức.',
    heroDisclaimer: 'Tất cả bản chuyển ngữ tiếng Anh và bản dịch đều do AI tạo ra và có thể chứa lỗi hoặc thiếu chính xác. Đây không phải là tài liệu chính thức và không nên được coi là có giá trị pháp lý ràng buộc. Luôn tham khảo tài liệu gốc từ cơ quan nhà ở để sử dụng chính thức. HousingAI không chịu trách nhiệm về bất kỳ thiệt hại hoặc hậu quả nào phát sinh từ việc sử dụng các bản dịch này.',
    selectPrompt: 'Chọn một cơ quan nhà ở để bắt đầu:',
    housingAuthority: 'Cơ Quan Nhà Ở',
    allRights: 'Bảo lưu mọi quyền.',
    searchPlaceholder: 'Tìm kiếm cơ quan nhà ở…',
    noResults: 'Không tìm thấy cơ quan nhà ở',
  },
  ru: {
    portalBadge: 'Портал ИИ Переводов',
    heroTitle: 'Жилищные Ресурсы с ИИ Переводом',
    heroDescription: 'Мы собираем все общедоступные веб-страницы, формы и документы каждого жилищного управления, транскрибируем их на английский язык с помощью ИИ и переводим на более чем 10 языков. Каждый перевод включает параллельный просмотр и прямые ссылки на оригинальные официальные документы.',
    heroDisclaimer: 'Все транскрипции на английский язык и переводы созданы ИИ и могут содержать ошибки или неточности. Это не официальные документы, и они не должны рассматриваться как юридически обязывающие. Всегда обращайтесь к оригинальным документам жилищного управления для официального использования. HousingAI не несёт ответственности за любой ущерб или последствия, возникшие в результате использования этих переводов.',
    selectPrompt: 'Выберите жилищное управление для начала:',
    housingAuthority: 'Жилищное Управление',
    allRights: 'Все права защищены.',
    searchPlaceholder: 'Поиск жилищных управлений…',
    noResults: 'Жилищные управления не найдены',
  },
  ar: {
    portalBadge: 'بوابة الترجمة بالذكاء الاصطناعي',
    heroTitle: 'موارد الإسكان المترجمة بالذكاء الاصطناعي',
    heroDescription: 'نقوم بجمع جميع صفحات الويب والنماذج والمستندات العامة من كل هيئة إسكان، ونسخها إلى الإنجليزية باستخدام الذكاء الاصطناعي، وترجمتها إلى أكثر من 10 لغات. تتضمن كل ترجمة عرضًا جنبًا إلى جنب وروابط مباشرة إلى المستندات الرسمية الأصلية.',
    heroDisclaimer: 'جميع النسخ والترجمات الإنجليزية تم إنشاؤها بالذكاء الاصطناعي وقد تحتوي على أخطاء أو عدم دقة. هذه ليست مستندات رسمية ولا ينبغي الاعتماد عليها كوثائق ملزمة قانونيًا. ارجع دائمًا إلى المستندات الأصلية من هيئة الإسكان للاستخدام الرسمي. HousingAI غير مسؤولة عن أي أضرار أو عواقب ناتجة عن استخدام هذه الترجمات.',
    selectPrompt: 'اختر هيئة إسكان للبدء:',
    housingAuthority: 'هيئة الإسكان',
    allRights: 'جميع الحقوق محفوظة.',
    searchPlaceholder: 'ابحث عن هيئة إسكان…',
    noResults: 'لم يتم العثور على هيئة إسكان',
  },
  ht: {
    portalBadge: 'Pòtay Tradiksyon IA',
    heroTitle: 'Resous Lojman Tradui pa IA',
    heroDescription: 'Nou kolekte tout paj entènèt piblik, fòmilè ak dokiman nan chak otorite lojman, transkri yo an angle ak IA, epi tradui yo nan plis pase 10 lang. Chak tradiksyon gen yon afichaj kote pa kote ak lyen dirèk nan dokiman ofisyèl orijinal yo.',
    heroDisclaimer: 'Tout transkripsyon angle ak tradiksyon yo te kreye pa IA epi yo ka gen erè oswa enekzaktitid. Sa yo pa dokiman ofisyèl epi yo pa dwe konsidere kòm dokiman ki gen fòs legal. Toujou refere a dokiman orijinal yo nan otorite lojman an pou itilizasyon ofisyèl. HousingAI pa responsab pou okenn domaj oswa konsekans ki soti nan itilizasyon tradiksyon sa yo.',
    selectPrompt: 'Chwazi yon otorite lojman pou kòmanse:',
    housingAuthority: 'Otorite Lojman',
    allRights: 'Tout dwa rezève.',
    searchPlaceholder: 'Chèche otorite lojman…',
    noResults: 'Pa jwenn okenn otorite lojman',
  },
  pt: {
    portalBadge: 'Portal de Tradução IA',
    heroTitle: 'Recursos Habitacionais Traduzidos por IA',
    heroDescription: 'Coletamos todas as páginas web, formulários e documentos públicos de cada autoridade habitacional, transcrevemos para inglês usando IA e traduzimos para mais de 10 idiomas. Cada tradução inclui visualização lado a lado e links diretos para os documentos oficiais originais.',
    heroDisclaimer: 'Todas as transcrições em inglês e traduções são geradas por IA e podem conter erros ou imprecisões. Estes não são documentos oficiais e não devem ser considerados juridicamente vinculativos. Consulte sempre os documentos originais da autoridade habitacional para uso oficial. A HousingAI não é responsável por quaisquer danos ou consequências resultantes do uso destas traduções.',
    selectPrompt: 'Selecione uma autoridade habitacional para começar:',
    housingAuthority: 'Autoridade Habitacional',
    allRights: 'Todos os direitos reservados.',
    searchPlaceholder: 'Pesquisar autoridades habitacionais…',
    noResults: 'Nenhuma autoridade habitacional encontrada',
  },
  it: {
    portalBadge: 'Portale di Traduzione IA',
    heroTitle: 'Risorse Abitative Tradotte dall\'IA',
    heroDescription: 'Raccogliamo tutte le pagine web, i moduli e i documenti pubblici di ogni ente per l\'edilizia abitativa, li trascriviamo in inglese tramite IA e li traduciamo in oltre 10 lingue. Ogni traduzione include la visualizzazione affiancata e link diretti ai documenti ufficiali originali.',
    heroDisclaimer: 'Tutte le trascrizioni in inglese e le traduzioni sono generate dall\'IA e possono contenere errori o imprecisioni. Questi non sono documenti ufficiali e non devono essere considerati giuridicamente vincolanti. Fare sempre riferimento ai documenti originali dell\'ente per l\'edilizia abitativa per uso ufficiale. HousingAI non è responsabile per eventuali danni o conseguenze derivanti dall\'uso di queste traduzioni.',
    selectPrompt: 'Seleziona un ente per l\'edilizia abitativa per iniziare:',
    housingAuthority: 'Ente per l\'Edilizia Abitativa',
    allRights: 'Tutti i diritti riservati.',
    searchPlaceholder: 'Cerca enti per l\'edilizia abitativa…',
    noResults: 'Nessun ente per l\'edilizia abitativa trovato',
  },
  el: {
    portalBadge: 'Πύλη Μετάφρασης AI',
    heroTitle: 'Στεγαστικοί Πόροι Μεταφρασμένοι από AI',
    heroDescription: 'Συλλέγουμε όλες τις δημόσιες ιστοσελίδες, φόρμες και έγγραφα κάθε οργανισμού στέγασης, τα μεταγράφουμε στα αγγλικά με AI και τα μεταφράζουμε σε πάνω από 10 γλώσσες. Κάθε μετάφραση περιλαμβάνει παράλληλη προβολή και απευθείας συνδέσμους προς τα επίσημα πρωτότυπα έγγραφα.',
    heroDisclaimer: 'Όλες οι μεταγραφές στα αγγλικά και οι μεταφράσεις δημιουργούνται από AI και μπορεί να περιέχουν σφάλματα ή ανακρίβειες. Δεν αποτελούν επίσημα έγγραφα και δεν πρέπει να θεωρούνται νομικά δεσμευτικά. Ανατρέχετε πάντα στα πρωτότυπα έγγραφα του οργανισμού στέγασης για επίσημη χρήση. Η HousingAI δεν φέρει ευθύνη για τυχόν ζημίες ή συνέπειες που προκύπτουν από τη χρήση αυτών των μεταφράσεων.',
    selectPrompt: 'Επιλέξτε έναν οργανισμό στέγασης για να ξεκινήσετε:',
    housingAuthority: 'Οργανισμός Στέγασης',
    allRights: 'Με επιφύλαξη παντός δικαιώματος.',
    searchPlaceholder: 'Αναζήτηση οργανισμών στέγασης…',
    noResults: 'Δεν βρέθηκαν οργανισμοί στέγασης',
  },
};

// Add new orgs here as you onboard them
const HOUSING_AUTHORITIES = [
  { slug: 'winchester', name: 'Winchester', state: 'MA' },
  { slug: 'salem', name: 'Salem', state: 'MA' },
  { slug: 'concord', name: 'Concord', state: 'MA' },
  { slug: 'arlington', name: 'Arlington', state: 'MA' },
  { slug: 'waltham', name: 'Waltham', state: 'MA' },
  { slug: 'marblehead', name: 'Marblehead', state: 'MA', ext: 'svg' },
  { slug: 'pittsfield', name: 'Pittsfield', state: 'MA', ext: 'webp' },
{ slug: 'foxboro', name: 'Foxborough', state: 'MA' },
  { slug: 'somerville', name: 'Somerville', state: 'MA', ext: 'jpg' },
  { slug: 'whitman', name: 'Whitman', state: 'MA' },
  { slug: 'andover', name: 'Andover', state: 'MA' },
  { slug: 'bunkie', name: 'Bunkie', state: 'LA' },
  { slug: 'brick', name: 'Brick', state: 'NJ' },
  { slug: 'hadley', name: 'Hadley', state: 'MA' },
  { slug: 'sarasota', name: 'Sarasota', state: 'FL' },
  // Quincy manages Hingham, Marshfield, Holbrook and Hanson; each has its own
  // site, assistant and library.
  { slug: 'quincy', name: 'Quincy', state: 'MA' },
  { slug: 'hingham', name: 'Hingham', state: 'MA' },
  { slug: 'marshfield', name: 'Marshfield', state: 'MA' },
  { slug: 'holbrook', name: 'Holbrook', state: 'MA' },
  { slug: 'hanson', name: 'Hanson', state: 'MA' },
  // { slug: 'worcester', name: 'Worcester', state: 'MA' },
];

const STATE_NAMES = {
  MA: 'Massachusetts',
  LA: 'Louisiana',
  NJ: 'New Jersey',
  FL: 'Florida',
};

// Grouped by state (states A→Z by full name), alphabetical within each state.
function groupAuthorities(query) {
  const q = (query || '').toLowerCase().trim();
  const matches = HOUSING_AUTHORITIES.filter(ha =>
    !q || `${ha.name} housing authority ${ha.state} ${STATE_NAMES[ha.state] || ''} ${ha.slug}`.toLowerCase().includes(q)
  );
  const byState = {};
  for (const ha of matches) (byState[ha.state] = byState[ha.state] || []).push(ha);
  return Object.keys(byState)
    .sort((a, b) => (STATE_NAMES[a] || a).localeCompare(STATE_NAMES[b] || b))
    .map(state => ({
      state,
      stateName: STATE_NAMES[state] || state,
      authorities: byState[state].sort((a, b) => a.name.localeCompare(b.name)),
    }));
}

export default function TranslateHubPage() {
  const navigate = useNavigate();
  const [lang, setLang] = useState(() => {
    // If navigated back from a translation page, preserve that language
    const match = document.referrer?.match(/\/translate\/\w+\/(\w{2})/);
    if (match && LANGUAGE_NAMES[match[1]]) return match[1];
    // Check if language was passed via navigation state
    try {
      const navState = window.history.state?.usr;
      if (navState?.lang && LANGUAGE_NAMES[navState.lang]) return navState.lang;
    } catch {}
    return 'en';
  });

  const labels = HUB_LABELS[lang] || HUB_LABELS.en;
  const isRTL = lang === 'ar';
  const mobile = useIsMobile();

  const [search, setSearch] = useState('');
  const groups = groupAuthorities(search);

  const cardHover = (e, hovering) => {
    e.currentTarget.style.borderColor = hovering ? T.blue600 : T.gray200;
    e.currentTarget.style.transform = hovering ? 'translateY(-2px)' : 'translateY(0)';
    e.currentTarget.style.boxShadow = hovering ? T.shadow : T.shadowSm;
  };

  return (
    <div style={{
      fontFamily: T.font,
      minHeight: '100vh',
      background: T.gradient,
      display: 'flex',
      flexDirection: 'column',
      direction: isRTL ? 'rtl' : 'ltr',
    }}>
      {/* Header */}
      <header style={{
        background: 'rgba(255,255,255,0.92)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${T.gray200}`,
        padding: mobile ? '0.75rem 1rem' : '1.25rem 1.5rem',
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: mobile ? 'flex-start' : 'center',
          gap: mobile ? '0.5rem' : '0.75rem',
          maxWidth: '1400px',
          margin: '0 auto',
          position: 'relative',
        }}>
          {/* Back button + logo row */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            width: mobile ? '100%' : 'auto',
          }}>
            {mobile && (
              <button
                onClick={() => navigate('/')}
                style={{
                  background: T.gray100,
                  border: `1px solid ${T.gray200}`,
                  borderRadius: T.radiusPill,
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  color: T.gray600,
                  padding: '0.45rem 0.85rem',
                  transition: 'all 0.15s ease',
                  fontFamily: T.font,
                  fontWeight: 600,
                  flexShrink: 0,
                }}
                title="Back to Home"
                onMouseOver={e => { e.currentTarget.style.background = T.blue50; e.currentTarget.style.borderColor = T.blue600; e.currentTarget.style.color = T.blue600; }}
                onMouseOut={e => { e.currentTarget.style.background = T.gray100; e.currentTarget.style.borderColor = T.gray200; e.currentTarget.style.color = T.gray600; }}
              >
                ←
              </button>
            )}
            {!mobile && (
              <button
                onClick={() => navigate('/')}
                style={{
                  position: 'absolute',
                  left: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: T.gray100,
                  border: `1px solid ${T.gray200}`,
                  borderRadius: T.radiusPill,
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  color: T.gray600,
                  padding: '0.45rem 0.85rem',
                  transition: 'all 0.15s ease',
                  fontFamily: T.font,
                  fontWeight: 600,
                }}
                title="Back to Home"
                onMouseOver={e => { e.currentTarget.style.background = T.blue50; e.currentTarget.style.borderColor = T.blue600; e.currentTarget.style.color = T.blue600; }}
                onMouseOut={e => { e.currentTarget.style.background = T.gray100; e.currentTarget.style.borderColor = T.gray200; e.currentTarget.style.color = T.gray600; }}
              >
                ←
              </button>
            )}
            {/* Logo + badge */}
            <Brand as="a" href="https://gethousingai.com" target="_blank" rel="noopener noreferrer" theme="light" markClassName="text-blue-600"
            style={{ fontWeight: 700, color: T.gray900, fontSize: mobile ? '1.1rem' : '1.35rem', fontFamily: T.font, textDecoration: 'none' }} />
            <span style={{ color: '#d1d5db', fontSize: '1.5rem', fontWeight: 300 }}>|</span>
            <span style={{
              fontSize: mobile ? '0.72rem' : '0.85rem',
              padding: mobile ? '0.2rem 0.55rem' : '0.25rem 0.75rem',
              background: T.blue100,
              color: T.blue600,
              borderRadius: T.radiusPill,
              fontWeight: 600,
            }}>
              {labels.portalBadge}
            </span>
          </div>

          {/* Language picker */}
          <div style={{
            display: 'flex',
            gap: '0.35rem',
            flexWrap: mobile ? 'nowrap' : 'wrap',
            justifyContent: mobile ? 'flex-start' : 'center',
            overflowX: mobile ? 'auto' : 'visible',
            WebkitOverflowScrolling: 'touch',
            maxWidth: '100%',
            paddingBottom: mobile ? '4px' : 0,
          }}>
            {ALL_LANGS.map(code => {
              const isActive = code === lang;
              return (
                <button
                  key={code}
                  onClick={() => setLang(code)}
                onClickCapture={() => trackTranslateHubVisit(code)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.35rem',
                    padding: mobile ? '0.25rem 0.55rem' : '0.3rem 0.65rem',
                    fontSize: mobile ? '0.75rem' : '0.82rem',
                    border: isActive ? `2px solid ${T.blue600}` : `1px solid ${T.gray200}`,
                    borderRadius: T.radiusPill,
                    cursor: 'pointer',
                    background: isActive ? T.blue50 : T.white,
                    color: isActive ? T.blue600 : T.gray500,
                    fontWeight: isActive ? 700 : 400,
                    transition: 'all 0.2s ease',
                    fontFamily: T.font,
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  <FlagImg lang={code} size={mobile ? 14 : 16} /> {LANGUAGE_NAMES[code]}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      {/* Main */}
      <main style={{ padding: mobile ? '1.25rem 0.75rem' : '2.5rem 1.5rem', flex: 1 }}>
        <div style={{ maxWidth: '1024px', margin: '0 auto' }}>
          {/* Hero */}
          <div style={{
            textAlign: 'center',
            marginBottom: mobile ? '1.5rem' : '2.5rem',
            padding: mobile ? '1.5rem 1rem' : '2.5rem 2rem',
            background: T.gradient,
            borderRadius: T.radius,
            boxShadow: T.shadow,
          }}>
            <h1 style={{
              fontSize: mobile ? '1.35rem' : '2rem',
              fontWeight: 800,
              color: T.gray900,
              fontFamily: T.font,
              marginBottom: '0.75rem',
              letterSpacing: '-0.02em',
            }}>
              {labels.heroTitle}
            </h1>
            <p style={{
              color: T.gray600,
              fontSize: mobile ? '0.84rem' : '0.92rem',
              maxWidth: '460px',
              margin: '0 auto',
              lineHeight: 1.65,
              marginBottom: '1rem',
            }}>
              {labels.heroDescription}
            </p>
            <p style={{
              color: T.gray500,
              fontSize: mobile ? '0.74rem' : '0.78rem',
              maxWidth: '460px',
              margin: '0 auto',
              lineHeight: 1.5,
              fontStyle: 'italic',
              padding: mobile ? '0.6rem 0.75rem' : '0.75rem 1rem',
              background: 'rgba(245,158,11,0.08)',
              borderRadius: T.radiusSm,
              border: '1px solid rgba(245,158,11,0.15)',
            }}>
              {labels.heroDisclaimer}
            </p>
          </div>

          {/* Search — same style as the document library search */}
          <div style={{ marginBottom: '1.5rem', position: 'relative' }}>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={labels.searchPlaceholder || HUB_LABELS.en.searchPlaceholder}
              style={{
                width: '100%',
                padding: isRTL ? '0.75rem 2.75rem 0.75rem 1rem' : '0.75rem 1rem 0.75rem 2.75rem',
                fontSize: '0.95rem',
                border: `1px solid ${T.gray200}`,
                borderRadius: T.radiusPill,
                outline: 'none',
                fontFamily: T.font,
                color: T.gray900,
                background: T.white,
                boxShadow: T.shadowSm,
                boxSizing: 'border-box',
                transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                direction: isRTL ? 'rtl' : 'ltr',
              }}
              onFocus={e => { e.target.style.borderColor = T.blue600; e.target.style.boxShadow = `0 0 0 3px ${T.blue50}`; }}
              onBlur={e => { e.target.style.borderColor = T.gray200; e.target.style.boxShadow = T.shadowSm; }}
            />
            <svg style={{ position: 'absolute', [isRTL ? 'right' : 'left']: '1rem', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.gray400} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            {search && (
              <button
                onClick={() => setSearch('')}
                style={{
                  position: 'absolute', [isRTL ? 'left' : 'right']: '0.75rem', top: '50%', transform: 'translateY(-50%)',
                  background: T.gray100, border: 'none', borderRadius: '50%',
                  width: '22px', height: '22px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.75rem', color: T.gray500, fontWeight: 700,
                }}
              >×</button>
            )}
          </div>

          {/* No results */}
          {groups.length === 0 && (
            <p style={{ textAlign: 'center', color: T.gray500, padding: '2rem 0', fontSize: '0.9rem' }}>
              {labels.noResults || HUB_LABELS.en.noResults}
            </p>
          )}

          {/* Org Cards — grouped by state, alphabetical within each */}
          {groups.length > 0 && (
            <p style={{
              fontSize: mobile ? '0.85rem' : '0.9rem',
              fontWeight: 600,
              color: T.gray800,
              marginBottom: '0.75rem',
            }}>
              {labels.selectPrompt}
            </p>
          )}
          {groups.map(group => (
            <div key={group.state} style={{ marginBottom: '1.25rem' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.6rem',
                margin: '0 0 0.6rem',
              }}>
                <span style={{ fontSize: mobile ? '0.8rem' : '0.85rem', fontWeight: 800, color: T.gray900, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                  {group.stateName}
                </span>
                <span style={{ flex: 1, height: '1px', background: T.gray200 }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {group.authorities.map(ha => (
                  <button
                    key={ha.slug}
                    onClick={() => { trackTranslateOrgSelect(ha.slug, lang); navigate(`/translate/${ha.slug}/${lang}`); }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: mobile ? '0.75rem' : '1rem',
                      padding: mobile ? '0.85rem 1rem' : '1.25rem 1.5rem',
                      background: T.white,
                      border: `1px solid ${T.gray200}`,
                      borderRadius: T.radiusSm,
                      cursor: 'pointer',
                      textAlign: isRTL ? 'right' : 'left',
                      transition: 'all 0.2s ease',
                      width: '100%',
                      fontFamily: T.font,
                      boxShadow: T.shadowSm,
                      direction: isRTL ? 'rtl' : 'ltr',
                    }}
                    onMouseOver={e => cardHover(e, true)}
                    onMouseOut={e => cardHover(e, false)}
                  >
                    <img
                      src={`/${ha.slug}-logo.${ha.ext || 'png'}`}
                      alt=""
                      style={{ height: mobile ? '32px' : '40px', width: mobile ? '32px' : '40px', objectFit: 'contain', borderRadius: '6px', flexShrink: 0 }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, color: T.gray900, fontSize: mobile ? '0.92rem' : '1.05rem' }}>
                        {ha.name} {labels.housingAuthority}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: T.gray600, marginTop: '0.15rem' }}>
                        {ha.state}
                      </div>
                    </div>
                    <span style={{ fontSize: '1.1rem', color: T.blue600, fontWeight: 600, flexShrink: 0 }}>{isRTL ? '←' : '→'}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </main>

      {/* Footer */}
      <footer style={{ background: T.gray900, color: T.white, padding: mobile ? '1.25rem 0' : '2rem 0', fontFamily: T.font }}>
        <div style={{
          maxWidth: '1200px', margin: '0 auto', padding: '0 1rem',
          display: 'flex', flexDirection: mobile ? 'column' : 'row',
          flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center',
          gap: mobile ? '0.5rem' : '1rem',
        }}>
          <Brand theme="dark" markClassName="text-blue-600"
            style={{ fontWeight: 600, fontSize: '0.95rem' }} />
          <div style={{ fontSize: mobile ? '0.78rem' : '0.875rem', color: '#d1d5db' }}>
            © 2026 HousingAI. {labels.allRights}
          </div>
        </div>
      </footer>
    </div>
  );
}